import React from "react";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/LanguageContext";

/**
 * TaxCategoryField — the {tax_category, exemption_citation, sac_code} triplet embedded
 * in every offering editor (booking services, telehealth services, courses, programs,
 * stripe products). Absence-tolerant by construction: `value` fields default upstream
 * (`tax_category || "standard"`, etc.) so a backend that hasn't started sending these
 * fields yet renders "standard" with no citation/SAC inputs — i.e. exactly today's UI.
 *
 * `country` only ever gates the SAC-code input (India GST). It comes from
 * useTaxProfile() in the parent, which is undefined while loading and null when the
 * tenant has no tax_profile yet — either way `country !== "IN"` so SAC just stays
 * hidden, never shown wrongly.
 */
export default function TaxCategoryField({ value, onChange, country }) {
  const t = useT("admin");
  const taxCategory = value?.tax_category || "standard";
  const exempt = taxCategory === "exempt";

  return (
    <div className="space-y-3" data-testid="tax-category-field">
      <div>
        <label className="text-xs">{t("taxcat.label")}</label>
        <select
          value={taxCategory}
          onChange={(e) => onChange({ ...value, tax_category: e.target.value })}
          data-testid="tax-category"
          className="w-full border rounded-md h-10 px-3 text-sm mt-1.5 bg-white"
        >
          <option value="standard">{t("taxcat.standard")}</option>
          <option value="exempt">{t("taxcat.exempt")}</option>
        </select>
      </div>

      {exempt && (
        <div>
          <label className="text-xs">{t("taxcat.citationLabel")}</label>
          <Input
            value={value?.exemption_citation || ""}
            onChange={(e) => onChange({ ...value, exemption_citation: e.target.value })}
            placeholder={t("taxcat.citationPh")}
            data-testid="tax-exemption-citation"
            className="mt-1.5"
          />
        </div>
      )}

      {country === "IN" && (
        <div>
          <label className="text-xs">{t("taxcat.sacLabel")}</label>
          <Input
            value={value?.sac_code || ""}
            onChange={(e) => onChange({ ...value, sac_code: e.target.value })}
            placeholder={t("taxcat.sacPh")}
            data-testid="tax-sac-code"
            className="mt-1.5"
          />
        </div>
      )}
    </div>
  );
}
